<template>
  <div class="physicians-list px-3">
    <div
      v-if="!physicians || physicians.length === 0"
      class="mb-0 border-bottom pb-2"
    >
      No physicians added yet.
    </div>
    <div
      class="
        physicians-card-header
        d-flex
        flex-wrap
        align-items-center
        mb-2
        justify-content-end
      "
      v-if="physicians && physicians.length"
    >
      <small class="text-muted mr-2"
        >(changes are staged locally — click Update in the bottom of the page to
        apply)</small
      >
      <button
        type="button"
        class="btn btn-sm btn-link text-black"
        @click="toggleAll"
        :disabled="disabled"
        v-if="physicians.length > 1"
      >
        <i
          class="fas"
          :class="allExpanded ? 'fa-compress mr-2' : 'fa-expand mr-2'"
          aria-hidden="true"
        ></i>
        {{ allExpanded ? "Collapse All" : "Expand All" }}
      </button>
    </div>
    <div
      v-for="(phys, idx) in physicians"
      :key="idx"
      class="physician-item-wrapper mb-2"
      :ref="setItemRef"
    >
      <PhysicianItem
        :physician="phys"
        :index="idx"
        :disabled="disabled"
        :expanded="allExpanded || phys.__expanded"
        @change="onChange(idx, $event)"
        @remove="onRemove"
        @toggle-expanded="onChildToggleExpanded(idx)"
      />
    </div>

    <div class="mt-3 d-flex justify-content-center">
      <button
        type="button"
        class="btn btn-black btn-sm"
        :disabled="disabled"
        @click="addPhysician"
      >
        <i class="fas fa-plus mr-2" aria-hidden="true"></i> Add Physician
      </button>
    </div>
  </div>
</template>
<script>
import PhysicianItem from "./PhysicianItem.vue";

export default {
  name: "PhysiciansList",
  components: { PhysicianItem },
  props: {
    physicians: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    allExpanded() {
      return (
        Array.isArray(this.physicians) &&
        this.physicians.length > 0 &&
        this.physicians.every((p) => !!p.__expanded)
      );
    },
  },
  methods: {
    addPhysician() {
      const newPhys = {
        name: "",
        type: "",
        phone: "",
        email: "",
        is_primary: false,
        __expanded: true,
      };
      const copy = [...this.physicians, newPhys];
      this.$emit("update:physicians", copy);
      this.$emit("add", newPhys);
      // scroll to new item after DOM update
      this.$nextTick(() => {
        const refs = this._itemRefs || [];
        const el = refs[refs.length - 1];
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
    setItemRef(el) {
      if (!this._itemRefs) this._itemRefs = [];
      if (el) this._itemRefs.push(el);
    },
    onChange(index, updated) {
      const copy = this.physicians.slice();
      // preserve any internal __expanded flag unless updated provides it
      const existing = copy[index] || {};
      const merged = Object.assign({}, existing, updated);
      copy.splice(index, 1, merged);
      this.$emit("update:physicians", copy);
      this.$emit("edit", { index, physician: merged });
    },
    onRemove(index) {
      const copy = this.physicians.slice();
      copy.splice(index, 1);
      this.$emit("update:physicians", copy);
      this.$emit("remove", index);
    },
    toggleAll() {
      const shouldExpand = !this.allExpanded;
      const copy = (this.physicians || []).slice();
      for (let i = 0; i < copy.length; i++) {
        const item = Object.assign({}, copy[i] || {});
        item.__expanded = shouldExpand;
        copy.splice(i, 1, item);
      }
      this.$emit("update:physicians", copy);
    },
    onChildToggleExpanded(index) {
      const copy = this.physicians.slice();
      const item = Object.assign({}, copy[index] || {});
      item.__expanded = !item.__expanded;
      copy.splice(index, 1, item);
      this.$emit("update:physicians", copy);
    },
  },
  beforeUpdate() {
    // reset collected refs so setItemRef can rebuild the current list
    this._itemRefs = [];
  },
  beforeDestroy() {
    this._itemRefs = null;
  },
};
</script>

<style scoped>
.physicians-card {
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  background: #fff;
}

.physicians-card-header {
  border-bottom: 1px solid #f1f1f1;
  padding-bottom: 0.5rem;
}

.physician-item-wrapper {
  border: 1px solid #e6e6e6;
  padding: 0.5rem;
  border-radius: 4px;
}
</style>
